import * as React from "react";
import AppIcon from "common/app-icon";
import { useTheme } from "styled-components";
import { RuntimeTheme } from "webapp/lib/theme/default-theme";
import { TimerStyle, ButtonRow } from "./stopwatch-style";
import { getNowTimeZoneAgnostic } from "common/Support";

const RADIUS = 80;

type StartPauseText = "START" | "STOP";
type StartStopIcons = "52-arrow-button-right-2" | "15-controls-pause";
interface Props {
    readonly onStartResume: () => void;
    readonly onPause: () => void;
    readonly onReset: () => void;
    readonly startTimeValue: Date | undefined;
    readonly durationValue: number | undefined;
    readonly enabled: boolean;
}
const Timer: React.FunctionComponent<Props> = p => {
    const { onStartResume, onPause, onReset, startTimeValue, durationValue, enabled } = p;
    const theme = useTheme() as RuntimeTheme;

    const [elapsedTime, setElapsedTime] = React.useState<number>(0);

    const [seconds, setSeconds] = React.useState<number>(0);
    const [minutes, setMinutes] = React.useState<number>(0);
    const [hours, setHours] = React.useState<number>(0);

    const [startPauseText, setStartPauseText] = React.useState<StartPauseText>(
        startTimeValue?.getTime() ? "STOP" : "START"
    );
    const [startPauseIcon, setStartPauseIcon] = React.useState<StartStopIcons>(
        startTimeValue?.getTime() ? "15-controls-pause" : "52-arrow-button-right-2"
    );

    const formatSingleDigits = (n: number): string => {
        return n > 9 ? "" + n : "0" + n;
    };

    const startStopHandler = React.useCallback(() => {
        if (startPauseText === "START") {
            onStartResume();
            setStartPauseText("STOP");
            setStartPauseIcon("15-controls-pause");
        } else if (startPauseText === "STOP") {
            onPause();
            setStartPauseText("START");
            setStartPauseIcon("52-arrow-button-right-2");
        }
    }, [onPause, onStartResume, startPauseText]);

    const resetHandler = React.useCallback(() => {
        onReset();
        setSeconds(0);
        setMinutes(0);
        setHours(0);
        setElapsedTime(0);
        setStartPauseIcon("52-arrow-button-right-2");
        setStartPauseText("START");
    }, [onReset]);

    const getLineLengthTimer = (line: number): number => {
        if (~~seconds === line) {
            return 70 - (seconds % 1) * 5;
        } else if (~~seconds < line) {
            return 70;
        } else return 65;
    };

    const getLineColor = (line: number): string => {
        if (line === 0) {
            if (seconds - 0.1 < line) return "#999999";
        }
        if (seconds <= line) {
            return "#999999";
        } else return theme.primaryAccentColor;
    };

    const calculateTimerRender = React.useCallback(() => {
        const sec = elapsedTime / 1000;
        const min = sec / 60;
        const hr = min / 60;
        setSeconds(sec % 60);
        setMinutes(min % 60);
        setHours(hr % 60);
    }, [elapsedTime]);

    const getTimerLines = () => {
        const lines = [];
        for (let i = 0; i < 60; i++) {
            lines.push(
                // Offset by 90 so that it starts from 12oclock position, but...
                // Translate before rotation, in that specific order.
                //
                // Here's the thing about the transform: CSS property, it's
                // applied right-to-left, because the translation matrix
                // multiplication is performed left-to-right as per the spec.
                //
                // Safari, as of December 2020, goes off of application
                // instead of matrix multiplication, which is not the right thing
                // to do as per the spec. One day it might perform these in the
                // correct order. In the meanwhile, we'll force everything to
                // perform correctly by moving the translation out into
                // a group.
                <line
                    key={`stopwatch-line-${i}`}
                    // grow length for completed times and upcoming times.
                    x1={getLineLengthTimer(i)}
                    x2={80}
                    style={{ stroke: getLineColor(i), strokeWidth: 2 }}
                    transform={`rotate(${i * 6 - 90})`}
                />
            );
        }
        return lines;
    };

    React.useEffect(() => {
        if (durationValue !== undefined && startTimeValue === undefined) {
            setElapsedTime(durationValue ?? 0);
        }

        if (startPauseText === "START" && seconds !== 0 && minutes !== 0) {
            return;
        }

        let startTime = getNowTimeZoneAgnostic().getTime() - elapsedTime;
        if (startTimeValue !== undefined) {
            startTime = startTimeValue.getTime() - (durationValue ?? 0);
        }
        const interval = setInterval(() => {
            setElapsedTime(getNowTimeZoneAgnostic().getTime() - startTime);
            calculateTimerRender();
        }, 10);
        return () => {
            clearInterval(interval);
        };
    }, [calculateTimerRender, elapsedTime, minutes, seconds, startPauseText, startTimeValue, durationValue]);

    return (
        <TimerStyle>
            <div className="timer-container">
                <svg height={RADIUS * 2 + 10} width={RADIUS * 2 + 10}>
                    <g transform={"translate(86 80)"}>{getTimerLines()}</g>
                </svg>
                <div className="timer-text-container">
                    <p
                        style={{ color: ~~hours === 0 ? "#999999" : theme.primaryAccentColor }}
                        className="timer-text">{`${formatSingleDigits(~~hours)}:`}</p>
                    <p
                        style={{ color: ~~hours === 0 && ~~minutes === 0 ? "#999999" : theme.primaryAccentColor }}
                        className="timer-text">{`${formatSingleDigits(~~minutes)}:`}</p>
                    <p
                        style={{
                            color:
                                ~~hours === 0 && ~~minutes === 0 && ~~seconds === 0
                                    ? "#999999"
                                    : theme.primaryAccentColor,
                        }}
                        className="timer-text">{`${formatSingleDigits(~~seconds)}`}</p>
                </div>
            </div>
            <ButtonRow>
                <button
                    disabled={!enabled || elapsedTime === 0 ? true : false}
                    onClick={resetHandler}
                    title="Reset Timer">
                    <AppIcon icon={"15-controls-stop"} color={"#ffffff"} size={15} />
                </button>
                <button
                    role="switch"
                    aria-checked={startPauseText === "STOP"}
                    disabled={!enabled}
                    onClick={startStopHandler}
                    title="Start/Stop Timer">
                    <AppIcon icon={startPauseIcon} color={"#ffffff"} size={15} />
                </button>
            </ButtonRow>
        </TimerStyle>
    );
};
export default Timer;
